import {
  DepositStatusId,
  DepositStatusIdType,
} from '@starsoft/common/models/deposit/status/id';
import { StepperOptions } from './props';
import {
  WithdrawalStatusId,
  WithdrawalStatusIdType,
} from '@starsoft/common/models/withdraw/status/id';

/**
 * Gets the translation key for deposit step title based on step position
 * @param {StepperOptions} step - The step position in the stepper (Created, Processing, Result)
 * @returns {string} Translation key for the step title
 * @example
 * getDepositStepTitle(StepperOptions.Created) // Returns 'deposit_order_created_title'
 */
export function getDepositStepTitle(step: StepperOptions) {
  switch (step) {
    case StepperOptions.Created:
      return 'deposit_order_created_title';
    case StepperOptions.Processing:
      return 'deposit_order_processing_title';
    case StepperOptions.Result:
      return 'deposit_order_result_title';
  }
}

/**
 * Gets the translation key for step description based on step position
 * @param {StepperOptions} step - The step position in the stepper (Created, Processing)
 * @returns {string} Translation key for the step description
 * @example
 * getStepDescription(StepperOptions.Created) // Returns 'deposit_order_created_description'
 */
export function getStepDescription(step: StepperOptions) {
  switch (step) {
    case StepperOptions.Created:
      return 'deposit_order_created_description';
    case StepperOptions.Processing:
      return 'deposit_order_processing_description';
  }
}

/**
 * Gets the translation key for withdrawal step title based on step position
 * @param {StepperOptions} step - The step position in the stepper (Created, Processing, Result)
 * @returns {string} Translation key for the step title
 * @example
 * getWithdrawStepTitle(StepperOptions.Created) // Returns 'withdraw_order_created_title'
 */
export function getWithdrawStepTitle(step: StepperOptions) {
  switch (step) {
    case StepperOptions.Created:
      return 'withdraw_order_created_title';
    case StepperOptions.Processing:
      return 'withdraw_order_processing_title';
    case StepperOptions.Result:
      return 'deposit_order_result_title';
  }
}

/**
 * Determines the status of a step based on its position and transaction status
 * @param {StepperOptions} step - The step position in the stepper
 * @param {DepositStatusIdType | WithdrawalStatusIdType} status - Current status of the transaction
 * @returns {string | null} Status indicator ('completed', 'current', 'error' or null)
 * @example
 * getStepperStatus(StepperOptions.Processing, DepositStatusId.Pending) // Returns 'current'
 * getStepperStatus(StepperOptions.Result, DepositStatusId.Paid) // Returns 'completed'
 */
export function getStepperStatus(
  step: StepperOptions,
  status: DepositStatusIdType | WithdrawalStatusIdType,
) {
  switch (step) {
    case StepperOptions.Created:
      return 'completed';
    case StepperOptions.Processing:
      switch (status?.toString()) {
        case DepositStatusId.Pending?.toString():
          return 'current';
        case DepositStatusId.Paid?.toString():
        case WithdrawalStatusId.Paid?.toString():
        case WithdrawalStatusId.Rejected?.toString():
        case DepositStatusId.Expired?.toString():
        case WithdrawalStatusId.Expired?.toString():
        case DepositStatusId.Refunded?.toString():
        case WithdrawalStatusId.Refunded?.toString():
          return 'completed';
      }
      break;
    case StepperOptions.Result:
      switch (status?.toString()) {
        case DepositStatusId.Pending?.toString():
        default:
          return null;
        case DepositStatusId.Paid?.toString():
        case WithdrawalStatusId.Paid?.toString():
          return 'completed';
        case DepositStatusId.Expired?.toString():
        case WithdrawalStatusId.Expired?.toString():
        case DepositStatusId.Refunded?.toString():
        case WithdrawalStatusId.Refunded?.toString():
          return 'error';
      }
  }
}

/**
 * Gets the appropriate Font Awesome icon class based on step position and transaction status
 * @param {StepperOptions} step - The step position in the stepper
 * @param {DepositStatusIdType | WithdrawalStatusIdType} status - Current status of the transaction
 * @returns {string} Font Awesome icon class name
 * @example
 * getSteperIcon(StepperOptions.Processing, DepositStatusId.Pending) // Returns 'fa-solid fa-rotate-right fa-spin'
 * getSteperIcon(StepperOptions.Result, DepositStatusId.Paid) // Returns 'fa-solid fa-check'
 */
export function getSteperIcon(
  step: StepperOptions,
  status: DepositStatusIdType | WithdrawalStatusIdType,
) {
  switch (step) {
    case StepperOptions.Created:
    default:
      return 'fa-solid fa-check';
    case StepperOptions.Processing:
      switch (status?.toString()) {
        case DepositStatusId.Pending?.toString():
          return 'fa-solid fa-rotate-right fa-spin';
        default:
          return 'fa-solid fa-check';
      }
    case StepperOptions.Result:
      switch (status?.toString()) {
        case DepositStatusId.Pending?.toString():
        case WithdrawalStatusId.Pending?.toString():
          return 'fa-solid fa-hourglass-half';
        case DepositStatusId.Paid?.toString():
        case WithdrawalStatusId.Paid?.toString():
          return 'fa-solid fa-check';
        case DepositStatusId.Expired?.toString():
        case WithdrawalStatusId.Expired?.toString():
        case DepositStatusId.Refunded?.toString():
        case WithdrawalStatusId.Refunded?.toString():
          return 'fa-solid fa-circle-exclamation';
        default:
          return 'fa-solid fa-hourglass-half';
      }
  }
}
